import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Lightning,
    Sparkle,
    EnvelopeSimple,
    VideoCamera,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { submitLead, formatApiError } from "@/lib/api";

const fadeUp = {
    initial: { y: 24, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Landing() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);

    // Hidden admin access: 7 taps on the footer copyright within 3 seconds
    const tapCountRef = useRef(0);
    const tapTimerRef = useRef(null);

    function handleSecretTap() {
        tapCountRef.current += 1;
        if (tapCountRef.current >= 7) {
            tapCountRef.current = 0;
            if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
            navigate("/admin/login");
            return;
        }
        if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
        tapTimerRef.current = setTimeout(() => {
            tapCountRef.current = 0;
        }, 3000);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Bitte gib deine Email ein");
            return;
        }
        setLoading(true);
        try {
            const res = await submitLead({ email: email.trim(), name: name.trim() || null });
            toast.success(res.message);
            navigate("/success", {
                state: { email: email.trim(), downloadUrl: res.download_url },
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Absenden");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="relative min-h-screen overflow-hidden" data-testid="landing-page">
            {/* Background texture (small, lazy) */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.08]">
                <img
                    src="https://images.unsplash.com/photo-1763805508094-901f2a79ff77?w=900&q=60&auto=format&fit=crop"
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                />
            </div>
            <div className="grain-overlay" />

            {/* Orange accent blobs */}
            <div className="pointer-events-none absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#FF5A00]/15 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-40 -left-40 h-[400px] w-[400px] rounded-full bg-[#FF5A00]/10 blur-[120px]" />

            {/* Top nav */}
            <nav className="relative z-10 flex items-center justify-between px-6 py-6 sm:px-10">
                <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-[#FF5A00] glow-orange" />
                    <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/60">
                        kylie.west
                    </span>
                </div>
            </nav>

            {/* Bento Grid */}
            <div className="relative z-10 mx-auto max-w-6xl px-6 pb-16 pt-4 sm:px-10">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-5">
                    {/* HERO TEXT — large card */}
                    <motion.div
                        {...fadeUp}
                        className="bento-card relative overflow-hidden p-8 sm:p-10 lg:col-span-8 lg:row-span-2"
                    >
                        <div className="label-eyebrow mb-6 flex items-center gap-2">
                            <span className="relative inline-flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                            </span>
                            online · jeden tag für dich
                        </div>
                        <h1 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
                            Hole dir JETZT <br />
                            ein
                            <span className="ml-3 inline-block rounded-2xl bg-[#FF5A00] px-3 pb-1 pt-2 text-black">
                                gratis Foto
                            </span>{" "}
                            ab.
                        </h1>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
                            Tauch ein in meine Welt — exklusiv & persönlich.
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <span className="label-eyebrow">scroll für dein foto</span>
                            <ArrowRight size={14} className="text-white/40" />
                        </div>
                    </motion.div>

                    {/* Portrait card */}
                    <motion.div
                        {...fadeUp}
                        transition={{ ...fadeUp.transition, delay: 0.05 }}
                        className="bento-card relative overflow-hidden p-0 lg:col-span-4 lg:row-span-2"
                    >
                        <img
                            src="https://customer-assets.emergentagent.com/job_bio-vault-4/artifacts/05cwkq1k_Kylie%20West%20Profil.jpg"
                            alt="Kylie West"
                            width="800"
                            height="1067"
                            fetchpriority="high"
                            decoding="async"
                            className="h-full min-h-[320px] w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                            <div className="label-eyebrow mb-1 text-white/60">creator</div>
                            <h3 className="font-display text-2xl font-bold text-white">
                                Kylie West
                            </h3>
                            <p className="font-mono text-xs text-white/50">
                                creator · 302K followers
                            </p>
                        </div>
                        <div className="absolute right-5 top-5 rounded-full bg-green-600 px-3 py-1">
                            <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-white">
                                online
                            </span>
                        </div>
                    </motion.div>

                    {/* Bio card */}
                    <motion.div
                        {...fadeUp}
                        transition={{ ...fadeUp.transition, delay: 0.08 }}
                        className="bento-card p-7 sm:p-8 lg:col-span-12"
                    >
                        <div className="label-eyebrow mb-3 flex items-center gap-2">
                            <VideoCamera size={12} weight="fill" className="text-[#FF5A00]" />
                            über mich
                        </div>
                        <p className="font-display text-lg leading-relaxed text-white sm:text-xl">
                            Mein Name ist <span className="text-[#FF5A00]">Kylie</span> und ich bin jeden Tag online für meine Community.
                            Hier bekommst du exklusive Einblicke und kannst Teil meiner privaten Welt werden.{" "}
                            <span className="text-white/60">Persönlich, direkt und ungefiltert.</span>
                        </p>
                    </motion.div>

                    {/* LEAD MAGNET — main capture card */}
                    <motion.div
                        {...fadeUp}
                        transition={{ ...fadeUp.transition, delay: 0.1 }}
                        className="bento-card relative overflow-hidden p-8 sm:p-10 lg:col-span-8"
                    >
                        <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[#FF5A00]/20 blur-3xl" />
                        <div className="relative">
                            <div className="label-eyebrow mb-4 flex items-center gap-2">
                                <Lightning size={12} weight="fill" className="text-[#FF5A00]" />
                                gratis · für die ersten 10.000
                            </div>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                                Gratis Foto von Kylie West
                            </h2>
                            <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/60 sm:text-base">
                                Du bekommst ein exklusives Foto aus meinem privaten Shoot.
                                Trag deine Email ein — ich schick&apos;s dir sofort.
                            </p>

                            <form onSubmit={handleSubmit} className="mt-8 space-y-3" data-testid="lead-magnet-form">
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <div className="relative flex-1">
                                        <input
                                            type="text"
                                            placeholder="Dein Name (optional)"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            autoComplete="given-name"
                                            className="w-full rounded-full border border-white/10 bg-black px-6 py-4 text-base text-white placeholder:text-white/30 outline-none transition focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00]"
                                            data-testid="name-input"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <div className="relative flex-1">
                                        <EnvelopeSimple
                                            size={18}
                                            className="absolute left-5 top-1/2 -translate-y-1/2 text-white/30"
                                        />
                                        <input
                                            type="email"
                                            required
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            autoComplete="email"
                                            inputMode="email"
                                            placeholder="deine@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full rounded-full border border-white/10 bg-black py-4 pl-12 pr-6 text-base text-white placeholder:text-white/30 outline-none transition focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00]"
                                            data-testid="email-input"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5A00] px-7 py-4 font-bold text-black transition-all duration-300 hover:-translate-y-1 hover:bg-[#FF7A33] hover:shadow-[0_8px_32px_rgba(255,90,0,0.35)] active:scale-95 disabled:opacity-60"
                                        data-testid="submit-lead-magnet"
                                    >
                                        {loading ? "Sende..." : "Foto sichern"}
                                        <ArrowRight size={18} weight="bold" className="transition group-hover:translate-x-1" />
                                    </button>
                                </div>
                                <p className="pl-2 font-mono text-[10px] uppercase tracking-widest text-white/30">
                                    kein spam · jederzeit abmelden
                                </p>
                                <p className="pl-2 text-[11px] leading-relaxed text-white/40">
                                    Mit dem Absenden willigst du ein, dass Kylie West dir per Email exklusive Inhalte, News und Angebote schickt. Abmeldung jederzeit möglich. Mehr dazu in der{" "}
                                    <Link to="/datenschutz" className="text-white/60 underline-offset-2 hover:text-[#FF5A00] hover:underline" data-testid="consent-privacy-link">
                                        Datenschutzerklärung
                                    </Link>
                                    .
                                </p>
                            </form>
                        </div>
                    </motion.div>

                    {/* Stats card */}
                    <motion.div
                        {...fadeUp}
                        transition={{ ...fadeUp.transition, delay: 0.15 }}
                        className="bento-card flex flex-col justify-between p-8 lg:col-span-4"
                    >
                        <div className="label-eyebrow flex items-center gap-2">
                            <Sparkle size={12} weight="fill" className="text-[#FF5A00]" />
                            in zahlen
                        </div>
                        <div className="mt-6 space-y-5">
                            <div>
                                <div className="font-display text-4xl font-black text-white">8M+</div>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/50">
                                    stream views
                                </div>
                            </div>
                            <div className="h-px w-full bg-white/10" />
                            <div>
                                <div className="font-display text-4xl font-black text-white">302K</div>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/50">
                                    followers
                                </div>
                            </div>
                            <div className="h-px w-full bg-white/10" />
                            <div>
                                <div className="font-display text-4xl font-black text-[#FF5A00]">7.500+</div>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/50">
                                    customers auf der liste
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Footer with hidden admin access (tap 7x within 3s) */}
                <p
                    onClick={handleSecretTap}
                    className="mt-12 cursor-default select-none text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/30"
                    data-testid="footer-secret"
                >
                    © {new Date().getFullYear()} Kylie West
                </p>
            </div>
        </div>
    );
}
